import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Download, Edit3, Plus, Trash2, Upload, FileDown, ChevronDown  } from 'lucide-react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE_URL } from "../config/apiconfig";
import { ui } from "../config/theme";



// PDF Styles (same as before)
const styles = StyleSheet.create({
    page: {
        padding: 40,
        backgroundColor: '#FFFFFF',
        fontFamily: 'Helvetica'
    },
    header: {
        marginBottom: 30,
        borderBottom: '1 solid #EAEAEA',
        paddingBottom: 20
    },
    name: {
        fontSize: 28,
        letterSpacing: 0.5,
        marginBottom: 8,
        color: '#1a365d',
        fontFamily: 'Helvetica-Bold'
    },
    title: {
        fontSize: 14,
        color: '#4a5568',
        marginBottom: 15,
        letterSpacing: 0.3
    },
    contactGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 15,
        marginTop: 10
    },
    contactInfo: {
        fontSize: 10,
        color: '#4a5568',
        marginRight: 20
    },
    section: {
        marginBottom: 25
    },
    sectionTitle: {
        fontSize: 16,
        fontFamily: 'Helvetica-Bold',
        color: '#1a365d',
        marginBottom: 12,
        paddingBottom: 4,
        borderBottom: '0.5 solid #E2E8F0'
    },
    summaryText: {
        fontSize: 11,
        lineHeight: 1.6,
        color: '#2d3748',
        textAlign: 'justify'
    },
    experienceBlock: {
        marginBottom: 15
    },
    companyHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 4
    },
    companyName: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        color: '#2d3748'
    },
    dateRange: {
        fontSize: 11,
        color: '#718096'
    },
    jobTitle: {
        fontSize: 12,
        color: '#4a5568',
        marginBottom: 4,
        fontFamily: 'Helvetica-Bold'
    },
    educationBlock: {
        marginBottom: 12
    },
    degree: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        color: '#2d3748',
        marginBottom: 2
    },
    institute: {
        fontSize: 11,
        color: '#4a5568',
        marginBottom: 2
    },
    skillsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 8
    },
    skillBadge: {
        fontSize: 10,
        backgroundColor: '#EBF4FF',
        color: '#2B6CB0',
        padding: '4 8',
        borderRadius: 4
    },
    languageBadge: {
        fontSize: 10,
        backgroundColor: '#F0FFF4',
        color: '#2F855A',
        padding: '4 8',
        borderRadius: 4
    },
    certificationBlock: {
        marginBottom: 10
    },
    certName: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        color: '#2d3748',
        marginBottom: 2
    },
    certIssuer: {
        fontSize: 11,
        color: '#4a5568'
    },
    certDate: {
        fontSize: 10,
        color: '#718096'
    }
});

const ProfilePDF = ({ profileData }) => (
    
    <Document>
        <Page size="A4" style={styles.page}>
            {/* Header Section */}
            <View style={styles.header}>
                <Text style={styles.name}>
                    {profileData.firstName} {profileData.lastName}
                </Text>
                {profileData.nic ? (
                    <Text style={styles.title}>NIC: {profileData.nic}</Text>
                ) : null}
                {profileData.applyingPosition && (
                <Text style={styles.title}>
                    {profileData.applyingPosition}
                </Text>
                    )}
                <View style={styles.contactGrid}>
                    {profileData.email && (
                        <Text style={styles.contactInfo}> {profileData.email}</Text>
                    )}
                    {profileData.phoneNumber && (
                        <Text style={styles.contactInfo}> {profileData.phoneCode} {profileData.phoneNumber}</Text>
                    )}
                    {profileData.nic && (
                        <Text style={styles.contactInfo}> NIC: {profileData.nic}</Text>
                    )}
                    {profileData.location && (
                        <Text style={styles.contactInfo}> {profileData.location}</Text>
                    )}
                </View>
            </View>

            

            {/* Professional Summary */}
            {profileData.summary && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Professional Summary</Text>
                    <Text style={styles.summaryText}>{profileData.summary}</Text>
                </View>
            )}

            {/* Work Experience */}
            {Array.isArray(profileData.careers) && profileData.careers.filter(Boolean).length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Professional Experience</Text>
                    {(profileData.careers || []).filter(Boolean).map((career, index) => (
                        <View key={index} style={styles.experienceBlock}>
                            <View style={styles.companyHeader}>
                                <Text style={styles.companyName}>{career?.companyName || ''}</Text>
                                <Text style={styles.dateRange}>
                                    {career?.startDate ? new Date(career.startDate).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        year: 'numeric' 
                                    }) : ''} - {
                                        career?.endDate ? 
                                        new Date(career.endDate).toLocaleDateString('en-US', { 
                                            month: 'short', 
                                            year: 'numeric' 
                                        }) : 'Present'
                                    }
                                </Text>
                            </View>
                            <Text style={styles.jobTitle}>{career?.jobPosition || ''}</Text>
                        </View>
                    ))}
                </View>
            )}

            {/* Education */}
            {Array.isArray(profileData.education) && profileData.education.filter(Boolean).length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Education</Text>
                    {(profileData.education || []).filter(Boolean).map((edu, index) => (
                        <View key={index} style={styles.educationBlock}>
                            <Text style={styles.degree}>{edu?.degreeProgram || ''}</Text>
                            <Text style={styles.institute}>{edu?.instituteName || ''}</Text>
                            <Text style={styles.dateRange}>
                                {edu?.startDate ? new Date(edu.startDate).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    year: 'numeric' 
                                }) : ''} - {
                                    edu?.endDate ? 
                                    new Date(edu.endDate).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        year: 'numeric' 
                                    }) : 'Present'
                                }
                            </Text>
                        </View>
                    ))}
                </View>
            )}

            {/* Certifications */}
            {Array.isArray(profileData.licenses) && profileData.licenses.filter(Boolean).length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Certifications</Text>
                    {(profileData.licenses || []).filter(Boolean).map((cert, index) => (
                        <View key={index} style={styles.certificationBlock}>
                            <Text style={styles.certName}>{cert?.name || ''}</Text>
                            <Text style={styles.certIssuer}>{cert?.issuer || ''}</Text>
                            <Text style={styles.certDate}>
                                {cert?.date ? `Issued: ${new Date(cert.date).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    year: 'numeric' 
                                })}` : ''}
                            </Text>
                        </View>
                    ))}
                </View>
            )}

            {/* Skills */}
            {Array.isArray(profileData.skills) && profileData.skills.filter(Boolean).length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Technical Skills</Text>
                    <View style={styles.skillsContainer}>
                        {(profileData.skills || []).filter(Boolean).map((skill, index) => (
                            <Text key={index} style={styles.skillBadge}>
                                {String((skill && typeof skill === 'object') ? (skill.name ?? '') : (skill ?? ''))}
                            </Text>
                        ))}
                    </View>
                </View>
            )}

            {/* Languages */}
            {Array.isArray(profileData.languages) && profileData.languages.filter(Boolean).length > 0 && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Languages</Text>
                    <View style={styles.skillsContainer}>
                        {(profileData.languages || []).filter(Boolean).map((language, index) => (
                            <Text key={index} style={styles.languageBadge}>
                                {String((language && typeof language === 'object') ? (language.name ?? '') : (language ?? ''))}
                            </Text>
                        ))}
                    </View>
                </View>
            )}

            {/* Garment Experience */}
            {(profileData.industry === 'Garment Sector') && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Garment Experience</Text>
                    {profileData.department ? (
                        <Text style={styles.summaryText}>Department: {profileData.department}</Text>
                    ) : null}
                    {profileData.position ? (
                        <Text style={styles.summaryText}>Designation: {profileData.position}</Text>
                    ) : null}

                    {Array.isArray(profileData.product_experiences) && profileData.product_experiences.filter(Boolean).length > 0 && (
                        <View style={{ marginTop: 10 }}>
                            {(profileData.product_experiences || []).filter(Boolean).map((pe, idx) => (
                                <View key={idx} style={styles.experienceBlock}>
                                    <Text style={styles.jobTitle}>Product: {pe?.product?.name || ''} {pe?.product?.code ? `(${pe.product.code})` : ''}</Text>
                                    <View style={styles.skillsContainer}>
                                        {(pe?.machines || []).filter(Boolean).map((m, mi) => (
                                            <Text key={mi} style={styles.skillBadge}>
                                                {(m?.name || '') + (m?.experience_level ? ` - ${m.experience_level}` : '')}
                                            </Text>
                                        ))}
                                    </View>
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            )}
        </Page>
    </Document>
);

// Helper function to format dates
function formatDate(date) {
    if (!date) return "Not Available";
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return new Date(date).toLocaleDateString('en-GB', options);
}

const Profile = () => {
    const [profilePicture, setProfilePicture] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState({
        firstName: "",
        lastName: "",
        location: "",
        phoneCode: "+94",
        phoneNumber: "",
        email: "",
        summary: "Add a personal summary to introduce yourself.",
        open_to_work: 0,
        careers: [],  // Change from object to empty array
        education: [],  // Change from object to empty array
        licenses: [],  // Change from object to empty array
        skills: [],
        languages: [],  // Change from object to empty array
        industry: "",
        department: "",
        position: "",
        products: [],
        machines: []
    });
    // Garment editor state (for edit side panel)
    const [productOptions, setProductOptions] = useState([]); // [{id, product_code, name}]
    const [entries, setEntries] = useState([{ productId: null, machineOptions: [], machineExperience: {} }]);
    const [deptOptions, setDeptOptions] = useState([]); // [{id, name}]
    const [desgOptions, setDesgOptions] = useState([]); // [{id, name}]

    // Sri Lanka hierarchical locations for Edit Profile
    const [lkLocations, setLkLocations] = useState([]);
    const [province, setProvince] = useState("");
    const [district, setDistrict] = useState("");
    const [divisionalSecretariat, setDivisionalSecretariat] = useState("");
    const [gnd, setGnd] = useState("");
    const [village, setVillage] = useState("");

    const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
        const formData = new FormData();
        formData.append('profile_picture', file);  // Changed from 'profilePicture' to 'profile_picture'
        
        const response = await fetch(`${API_BASE_URL}/profile/${user.email}/picture`, {
            method: 'POST',
            body: formData
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to upload profile picture');
        }
        
        // Update the profile picture in state
        setProfilePicture(file);
        alert('Profile picture uploaded successfully!');
        
        // Force refresh the profile picture by updating the timestamp
        setProfileData(prev => ({ ...prev, pictureUpdated: Date.now() }));
        
    } catch (error) {
        console.error('Error uploading profile picture:', error);
        alert('Error uploading profile picture. Please try again.');
    }
};

    const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    
    if (type === 'picture') {
        // Keep existing profile picture handling
    } else if (type === 'resume') {
        if (file && (
            file.type === 'application/pdf' || 
            file.type === 'application/msword' || 
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        )) {
            try {
                const formData = new FormData();
                formData.append('resume', file);
                
                const response = await fetch(`${API_BASE_URL}/profile/${user.email}/resume`, {
                    method: 'POST',
                    body: formData
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Failed to upload resume');
                }
                
                const result = await response.json();
                setResumeFile(file); // Keep the file object for UI display
                alert('Resume uploaded successfully!');
            } catch (error) {
                console.error('Error uploading resume:', error);
                alert('Error uploading resume. Please try again.');
            }
        } else {
            alert('Please upload a valid resume file (PDF or Word)');
        }
    }
};
    

    const [tempData, setTempData] = useState({});
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isPdfReady, setIsPdfReady] = useState(false);
    const [positionSuggestions, setPositionSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [positionInputValue, setPositionInputValue] = useState('');
    const isSriLanka = ((tempData.phoneCode ?? profileData.phoneCode) === '+94');

    const [togglingOpen, setTogglingOpen] = useState(false);

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                if (!user?.email) {
                    setLoading(false);
                    return;
                }
    
                const response = await fetch(`${API_BASE_URL}/profile/${user.email}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const data = await response.json();
                setProfileData(prevData => ({
                    ...prevData,
                    ...data,
                    careers: data.careers || prevData.careers,
                    education: data.education || prevData.education,
                    licenses: data.licenses || prevData.licenses,
                    skills: data.skills || prevData.skills,
                    languages: data.languages || prevData.languages,
                    industry: data.industry || prevData.industry || "",
                    department: data.department || prevData.department || "",
                    position: data.position || prevData.position || "",
                    products: Array.isArray(data.products) ? data.products : (prevData.products || []),
                    machines: Array.isArray(data.machines) ? data.machines : (prevData.machines || []),
                    open_to_work: typeof data.open_to_work === 'number' || typeof data.open_to_work === 'boolean' ? (data.open_to_work ? 1 : 0) : (prevData.open_to_work || 0)
                }));
                // Initialize LK hierarchy from backend values
                setProvince(data.province || "");
                setDistrict(data.district || "");
                setDivisionalSecretariat(data.divisional_secretariat || "");
                setGnd(data.grama_niladhari_division || "");
                setVillage(data.village || "");

                // Initialize garment editor entries from fetched relational data
                if (Array.isArray(data.product_experiences) && data.product_experiences.length > 0) {
                    const init = data.product_experiences.map(pe => ({
                        productId: pe.product?.id || null,
                        machineOptions: [],
                        machineExperience: (pe.machines || []).reduce((acc, m) => { acc[m.id] = m.experience_level || 'Beginner'; return acc; }, {})
                    }));
                    setEntries(init.length > 0 ? init : [{ productId: null, machineOptions: [], machineExperience: {} }]);
                } else if (Array.isArray(data.product_ids) && data.product_ids.length > 0) {
                    setEntries([{ productId: Number(data.product_ids[0]), machineOptions: [], machineExperience: {} }]);
                } else {
                    setEntries([{ productId: null, machineOptions: [], machineExperience: {} }]);
                }
                setIsPdfReady(true);
            } catch (err) {
                console.error('Error fetching profile:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
    
        fetchProfileData();
    }, [user]);

    // Load Sri Lanka hierarchical data when edit opens
    useEffect(() => {
        if (!isEditOpen) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch('https://general.apivcm.shop/sri-lanka-locations', { cache: 'no-store' });
                if (!r.ok) throw new Error('Failed to load Sri Lanka locations');
                const j = await r.json();
                if (!cancelled) setLkLocations(Array.isArray(j?.data) ? j.data : []);
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [isEditOpen]);

    // When switching away from LK, clear LK selections
    useEffect(() => {
        if (!isEditOpen) return;
        if (!isSriLanka) {
            setProvince(""); setDistrict(""); setDivisionalSecretariat(""); setGnd(""); setVillage("");
        }
    }, [isEditOpen, isSriLanka]);

    // Options derivation
    const provinceOptions = lkLocations.map(p => p.province);
    const districtOptions = (() => {
        const p = lkLocations.find(x => x.province === province);
        return p ? (p.districts || []).map(d => d.district) : [];
    })();
    const dsOptions = (() => {
        const p = lkLocations.find(x => x.province === province);
        const d = p ? (p.districts || []).find(x => x.district === district) : null;
        return d ? (d.divisional_secretariats || []).map(ds => ds.divisional_secretariat) : [];
    })();
    const gndOptions = (() => {
        const p = lkLocations.find(x => x.province === province);
        const d = p ? (p.districts || []).find(x => x.district === district) : null;
        const ds = d ? (d.divisional_secretariats || []).find(x => x.divisional_secretariat === divisionalSecretariat) : null;
        return ds ? (ds.grama_niladhari_divisions || []).map(g => g.grama_niladhari_division) : [];
    })();
    const villageOptions = (() => {
        const p = lkLocations.find(x => x.province === province);
        const d = p ? (p.districts || []).find(x => x.district === district) : null;
        const ds = d ? (d.divisional_secretariats || []).find(x => x.divisional_secretariat === divisionalSecretariat) : null;
        const g = ds ? (ds.grama_niladhari_divisions || []).find(x => x.grama_niladhari_division === gnd) : null;
        return g ? (g.villages || []).map(v => v.village_name) : [];
    })();

    // Compose human-readable location when LK is selected
    useEffect(() => {
        if (!isEditOpen) return;
        if (!isSriLanka) return;
        const parts = [village, gnd, divisionalSecretariat, district, province].filter(Boolean);
        handleFieldChange('location', parts.join(', '));
    }, [isEditOpen, isSriLanka, province, district, divisionalSecretariat, gnd, village]);

    // Fetch product options when edit opens and industry is garment
    useEffect(() => {
        if (!isEditOpen) return;
        if ((tempData.industry ?? profileData.industry) !== 'Garment Sector') return;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const r = await fetch(`${API_BASE_URL}/lookup/products`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (r.ok) setProductOptions(await r.json());
            } catch {}
        })();
    }, [isEditOpen, tempData.industry, profileData.industry]);

    // Fetch departments when edit opens for garment
    useEffect(() => {
        if (!isEditOpen) return;
        if ((tempData.industry ?? profileData.industry) !== 'Garment Sector') return;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const r = await fetch(`${API_BASE_URL}/lookup/departments`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (r.ok) setDeptOptions(await r.json());
            } catch {}
        })();
    }, [isEditOpen, tempData.industry, profileData.industry]);

    // Fetch designations when department changes
    useEffect(() => {
        if (!isEditOpen) return;
        if ((tempData.industry ?? profileData.industry) !== 'Garment Sector') return;
        const deptName = (tempData.department ?? profileData.department) || '';
        if (!deptName) { setDesgOptions([]); return; }
        const dept = deptOptions.find(d => d.name === deptName);
        if (!dept) { setDesgOptions([]); return; }
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const r = await fetch(`${API_BASE_URL}/lookup/designations?department_id=${dept.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                if (r.ok) setDesgOptions(await r.json());
            } catch {}
        })();
    }, [isEditOpen, tempData.department, profileData.department, deptOptions, tempData.industry, profileData.industry]);

    const loadMachinesForEntry = async (idx, productId) => {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        try {
            const r = await fetch(`${API_BASE_URL}/lookup/machines?product_id=${encodeURIComponent(productId)}`, { headers });
            if (!r.ok) return;
            const arr = await r.json();
            setEntries(prev => prev.map((e, i) => {
                if (i !== idx) return e;
                // Keep only experience entries for machines that still exist in options
                const validIds = new Set(arr.map(m => m.id));
                const preserved = Object.fromEntries(Object.entries(e.machineExperience || {}).filter(([mid]) => validIds.has(Number(mid))));
                return { ...e, machineOptions: arr, machineExperience: preserved };
            }));
        } catch {}
    };

    // Ensure machineOptions are loaded for entries with a selected product
    useEffect(() => {
        if (!isEditOpen) return;
        entries.forEach((e, idx) => {
            if (e.productId && (!e.machineOptions || e.machineOptions.length === 0)) {
                loadMachinesForEntry(idx, e.productId);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditOpen, entries.map(e=>e.productId).join(',')]);

    // Initialize position input value when edit panel opens
    useEffect(() => {
        if (isEditOpen) {
            // pause PDF rendering while editing to avoid react-pdf pagination on transient data
            setIsPdfReady(false);
            setPositionInputValue(tempData.applyingPosition || profileData.applyingPosition || '');
        } else {
            // resume PDF rendering when panel closes
            setIsPdfReady(true);
        }
    }, [isEditOpen, tempData.applyingPosition, profileData.applyingPosition]);


    const handleFieldChange = (field, value) => {
        setTempData((prev) => ({ 
            ...prev, 
            [field]: value === "" ? " " : value  // Store a space instead of empty string
        }));
    };

    // Function to fetch position suggestions from API
    const fetchPositionSuggestions = async (query) => {
        if (query.length < 2) {
            setPositionSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/positions/search?query=${encodeURIComponent(query)}`);
            if (response.ok) {
                const suggestions = await response.json();
                setPositionSuggestions(suggestions);
                setShowSuggestions(suggestions.length > 0);
            } else {
                setPositionSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error('Error fetching position suggestions:', error);
            setPositionSuggestions([]);
            setShowSuggestions(false);
        }
    };

    // Handle position input change with autocomplete
    const handlePositionInputChange = (value) => {
        setPositionInputValue(value);
        handleFieldChange('applyingPosition', value);
        fetchPositionSuggestions(value);
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (suggestion) => {
        setPositionInputValue(suggestion);
        handleFieldChange('applyingPosition', suggestion);
        setShowSuggestions(false);
        setPositionSuggestions([]);
    };

    const handleToggleOpenToWork = async () => {
        try {
            if (!user?.email) return;
            setTogglingOpen(true);
            const next = profileData.open_to_work ? 0 : 1;

            // Build relational payload from garment editor entries (reuse logic from saveAllChanges)
            const productIds = entries.map(e => e.productId).filter(Boolean);
            const machineExp = entries.flatMap(e => Object.entries(e.machineExperience || {}).map(([machine_id, experience_level]) => ({ machine_id: Number(machine_id), experience_level })));

            const cleanProfileData = {
                firstName: profileData.firstName || "",
                lastName: profileData.lastName || "",
                location: profileData.location || "",
                phoneCode: profileData.phoneCode || "+94",
                phoneNumber: profileData.phoneNumber || "",
                email: user.email,
                summary: profileData.summary || "",
                applyingPosition: profileData.applyingPosition || "",
                industry: profileData.industry || "",
                department: profileData.department || "",
                position: profileData.position || "",
                careers: Array.isArray(profileData.careers) ? profileData.careers : [],
                education: Array.isArray(profileData.education) ? profileData.education : [],
                licenses: Array.isArray(profileData.licenses) ? profileData.licenses : [],
                skills: Array.isArray(profileData.skills) ? profileData.skills : [],
                languages: Array.isArray(profileData.languages) ? profileData.languages : [],
                products: Array.isArray(profileData.products) ? profileData.products : [],
                machines: Array.isArray(profileData.machines) ? profileData.machines : [],
                product_ids: productIds,
                machine_experience: machineExp,
                open_to_work: next
            };

            const formData = new FormData();
            formData.append('profileData', JSON.stringify(cleanProfileData));
            const token = localStorage.getItem('token');
            const resp = await fetch(`${API_BASE_URL}/profile/${user.email}`, {
                method: 'PUT',
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                body: formData
            });
            if (!resp.ok) {
                let msg = 'Failed to update status';
                try { const j = await resp.json(); msg = j.error || j.message || msg; } catch {}
                throw new Error(msg);
            }
            setProfileData(prev => ({ ...prev, open_to_work: next }));
        } catch (e) {
            alert(e.message || 'Error updating Open to Work');
        } finally {
            setTogglingOpen(false);
        }
    };
    
    // Add new item handlers
    const addCareer = () => {
        setTempData(prev => ({
            ...prev,
            careers: [...(prev.careers || profileData.careers), {
                companyName: "",
                jobPosition: "",
                startDate: "",
                endDate: ""
            }]
        }));
    };

    const addEducation = () => {
        setTempData(prev => ({
            ...prev,
            education: [...(prev.education || profileData.education), {
                instituteName: "",
                degreeProgram: "",
                startDate: "",
                endDate: ""
            }]
        }));
    };

    const addLicense = () => {
        setTempData(prev => ({
            ...prev,
            licenses: [...(prev.licenses || profileData.licenses), {
                name: "",
                issuer: "",
                date: ""
            }]
        }));
    };

    // Remove item handlers
    const removeItem = (array, index, field) => {
        setTempData(prev => ({
            ...prev,
            [field]: array.filter((_, i) => i !== index)
        }));
    };

    // Handle array field changes
    const handleArrayFieldChange = (index, field, subField, value, arrayName) => {
        setTempData(prev => {
            const array = [...(prev[arrayName] || profileData[arrayName])];
            array[index] = {
                ...array[index],
                [subField]: value
            };
            return {
                ...prev,
                [arrayName]: array
            };
        });
    };

    
    const saveAllChanges = async () => {
        try {
            // prevent PDF re-render during save
            setIsPdfReady(false);
            if (!user?.email) {
                throw new Error('User email not available');
            }

            // Helper function to safely format dates
            const formatDateForSave = (dateString) => {
                if (!dateString) return null;
                try {
                    const date = new Date(dateString);
                    return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
                } catch (e) {
                    return null;
                }
            };

            // Create a clean data object without any undefined or circular references
            // Build relational payload from garment editor entries
            const productIds = entries.map(e => e.productId).filter(Boolean);
            const machineExp = entries.flatMap(e => Object.entries(e.machineExperience || {}).map(([machine_id, experience_level]) => ({ machine_id: Number(machine_id), experience_level })));

            const cleanProfileData = {
                firstName: tempData.firstName || profileData.firstName || "",
                lastName: tempData.lastName || profileData.lastName || "",
                nic: tempData.nic ?? profileData.nic ?? "", // Add NIC field with proper fallback
                location: tempData.location || profileData.location || "",
                phoneCode: tempData.phoneCode || profileData.phoneCode || "+94",
                phoneNumber: tempData.phoneNumber || profileData.phoneNumber || "",
                email: user.email,
                summary: tempData.summary || profileData.summary || "",
                applyingPosition: tempData.applyingPosition || profileData.applyingPosition || "",
                industry: (tempData.industry ?? profileData.industry) || "",
                department: tempData.department || profileData.department || "",
                position: tempData.position || profileData.position || "",
                // LK location fields
                province: isSriLanka ? (province || null) : null,
                district: isSriLanka ? (district || null) : null,
                divisional_secretariat: isSriLanka ? (divisionalSecretariat || null) : null,
                grama_niladhari_division: isSriLanka ? (gnd || null) : null,
                village: isSriLanka ? (village || null) : null,
                location_country: isSriLanka ? 'Sri Lanka' : null,
                
                // Handle arrays properly with safe date formatting
                careers: (tempData.careers || profileData.careers || []).map(career => ({
                    companyName: career.companyName || "",
                    jobPosition: career.jobPosition || "",
                    startDate: formatDateForSave(career.startDate),
                    endDate: formatDateForSave(career.endDate)
                })),
                    
                education: (tempData.education || profileData.education || []).map(edu => ({
                    instituteName: edu.instituteName || "",
                    degreeProgram: edu.degreeProgram || "",
                    startDate: formatDateForSave(edu.startDate),
                    endDate: formatDateForSave(edu.endDate)
                })),
                    
                licenses: (tempData.licenses || profileData.licenses || []).map(license => ({
                    name: license.name || "",
                    issuer: license.issuer || "",
                    date: formatDateForSave(license.date)
                })),
                    
                // Handle skills and languages properly
                skills: Array.isArray(tempData.skills) 
                    ? tempData.skills 
                    : (tempData.skills?.split(',').map(skill => skill.trim()) || 
                    (Array.isArray(profileData.skills) ? profileData.skills : [])),
                
                languages: Array.isArray(tempData.languages)
                    ? tempData.languages
                    : (tempData.languages?.split(',').map(lang => lang.trim()) || 
                    (Array.isArray(profileData.languages) ? profileData.languages : [])),

                // Legacy display arrays (kept for compatibility) - will be backfilled by backend too
                products: Array.isArray(profileData.products) ? profileData.products : [],
                machines: Array.isArray(profileData.machines) ? profileData.machines : [],

                // New relational fields
                product_ids: productIds,
                machine_experience: machineExp
            };

            // Create FormData for profile data only (NO profile picture handling)
            const formData = new FormData();
            formData.append('profileData', JSON.stringify(cleanProfileData));
            
            // REMOVED: Profile picture handling - it's handled separately
            
            // First save the profile data
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/profile/${user.email}`, {
                method: "PUT",
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                body: formData,
            });
            
            if (!response.ok) {
                let errorMessage;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || `Server error: ${response.status}`;
                } catch (e) {
                    errorMessage = `HTTP error: ${response.status}`;
                }
                throw new Error(errorMessage);
            }
            
            // Then handle resume upload separately if a new file was selected
            if (resumeFile && resumeFile instanceof File) {
                const resumeFormData = new FormData();
                resumeFormData.append('resume', resumeFile);
                
                const resumeResponse = await fetch(`${API_BASE_URL}/profile/${user.email}/resume`, {
                    method: 'POST',
                    body: resumeFormData
                });
                
                if (!resumeResponse.ok) {
                    const errorData = await resumeResponse.json();
                    throw new Error(errorData.error || 'Failed to upload resume');
                }
            }
            
            // The backend returns a minimal response, so we'll merge our sent data with the existing profile data
            // to ensure all fields (including NIC) are preserved in the UI
            await response.json();
            
            // Merge the data we sent with the existing profile data
            setProfileData(prev => ({
                ...prev,
                ...cleanProfileData,
                // Preserve any arrays that might not be in cleanProfileData
                careers: cleanProfileData.careers || prev.careers,
                education: cleanProfileData.education || prev.education,
                licenses: cleanProfileData.licenses || prev.licenses,
                skills: cleanProfileData.skills || prev.skills,
                languages: cleanProfileData.languages || prev.languages
            }));
            
            setIsEditOpen(false);
            // re-enable PDF rendering with updated data
            setIsPdfReady(true);
            alert('Profile updated successfully!');
            
            // No need to reload the page since we're updating the state properly
            // This prevents the UI from flashing and provides a smoother experience
            
        } catch (error) {
            console.error('Error saving changes:', error);
            alert(`Error saving changes: ${error.message}. Please try again.`);
        } finally {
            // ensure PDF is not left disabled
            setIsPdfReady(true);
        }
    };


    const renderEducationFields = () => (
        <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-xl font-semibold text-gray-800">Education</h3>
                <button 
                    className="text-indigo-600 hover:text-indigo-800"
                    onClick={addEducation}
                >
                    <Plus size={20} />
                </button>
            </div>
            {(tempData.education || profileData.education).map((education, index) => (
                <div key={index} className="mb-4 p-4 border rounded-lg relative">
                    <button 
                        className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                        onClick={() => removeItem(tempData.education || profileData.education, index, 'education')}
                    >
                        <Trash2 size={16} />
                    </button>
                    <div className="grid grid-cols-1 gap-4">
                        <input
                            type="text"
                            placeholder="Institute Name"
                            className="w-full p-2 border rounded"
                            value={education.instituteName}
                            onChange={(e) => handleArrayFieldChange(index, 'education', 'instituteName', e.target.value, 'education')}
                        />
                        <input
                            type="text"
                            placeholder="Degree Program"
                            className="w-full p-2 border rounded"
                            value={education.degreeProgram}
                            onChange={(e) => handleArrayFieldChange(index, 'education', 'degreeProgram', e.target.value, 'education')}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-gray-600">Start Date</label>
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded mt-1"
                                    value={education.startDate ?? ''}
                                    onChange={(e) => handleArrayFieldChange(index, 'education', 'startDate', e.target.value, 'education')}
                                />
                            </div>
                            <div>
                                <label className="text-sm text-gray-600">End Date</label>
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded mt-1"
                                    value={education.endDate ?? ''}
                                    onChange={(e) => handleArrayFieldChange(index, 'education', 'endDate', e.target.value, 'education')}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
    
    const renderLicenseFields = () => (
        <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-xl font-semibold text-gray-800">Licenses & Certifications</h3>
                <button 
                    className="text-indigo-600 hover:text-indigo-800"
                    onClick={addLicense}
                >
                    <Plus size={20} />
                </button>
            </div>
            {(tempData.licenses || profileData.licenses).map((license, index) => (
                <div key={index} className="mb-4 p-4 border rounded-lg relative">
                    <button 
                        className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                        onClick={() => removeItem(tempData.licenses || profileData.licenses, index, 'licenses')}
                    >
                        <Trash2 size={16} />
                    </button>
                    <div className="grid grid-cols-1 gap-4">
                        <input
                            type="text"
                            placeholder="License/Certificate Name"
                            className="w-full p-2 border rounded"
                            value={license.name}
                            onChange={(e) => handleArrayFieldChange(index, 'licenses', 'name', e.target.value, 'licenses')}
                        />
                        <input
                            type="text"
                            placeholder="Issuing Organization"
                            className="w-full p-2 border rounded"
                            value={license.issuer}
                            onChange={(e) => handleArrayFieldChange(index, 'licenses', 'issuer', e.target.value, 'licenses')}
                        />
                        <div>
                            <label className="text-sm text-gray-600">Issue Date</label>
                            <input
                                type="date"
                                className="w-full p-2 border rounded mt-1"
                                value={license.date ?? ''}
                                onChange={(e) => handleArrayFieldChange(index, 'licenses', 'date', e.target.value, 'licenses')}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderCareerFields = () => (
        <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-xl font-semibold text-gray-800">Career History</h3>
                <button 
                    className="text-indigo-600 hover:text-indigo-800"
                    onClick={addCareer}
                >
                    <Plus size={20} />
                </button>
            </div>
            {(tempData.careers || profileData.careers).map((career, index) => (
                <div key={index} className="mb-4 p-4 border rounded-lg relative">
                    <button 
                        className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                        onClick={() => removeItem(tempData.careers || profileData.careers, index, 'careers')}
                    >
                        <Trash2 size={16} />
                    </button>
                    <div className="grid grid-cols-1 gap-4">
                        <input
                            type="text"
                            placeholder="Company Name"
                            className="w-full p-2 border rounded"
                            value={career.companyName}
                            onChange={(e) => handleArrayFieldChange(index, 'careers', 'companyName', e.target.value, 'careers')}
                        />
                        <input
                            type="text"
                            placeholder="Position"
                            className="w-full p-2 border rounded"
                            value={career.jobPosition}
                            onChange={(e) => handleArrayFieldChange(index, 'careers', 'jobPosition', e.target.value, 'careers')}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <input
                                type="date"
                                className="w-full p-2 border rounded"
                                value={career.startDate ?? ''}
                                onChange={(e) => handleArrayFieldChange(index, 'careers', 'startDate', e.target.value, 'careers')}
                            />
                            <input
                                type="date"
                                className="w-full p-2 border rounded"
                                value={career.endDate ?? ''}
                                onChange={(e) => handleArrayFieldChange(index, 'careers', 'endDate', e.target.value, 'careers')}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const checkExistingResume = async () => {
        try {
            if (!user?.email) return;
            
            const response = await fetch(`${API_BASE_URL}/profile/${user.email}/resume`, {
                method: 'HEAD' // Just check if resume exists without downloading
            });
            
            if (response.ok) {
                // Resume exists and is valid
                setResumeFile({ name: 'Existing Resume' }); // Set some placeholder
            } else {
                setResumeFile(null);
            }
        } catch (error) {
            console.log('No existing resume found');
            setResumeFile(null);
        }
    };

    const handleResumeDownload = async () => {
        try {
            if (!user?.email) {
                throw new Error('Please log in to download resume');
            }
            
            const response = await fetch(`${API_BASE_URL}/profile/${user.email}/resume`);
            
            if (!response.ok) {
                const errorData = await response.json();
                
                if (errorData.message && errorData.message.includes('Invalid resume data')) {
                    const shouldCleanup = window.confirm(
                        'Your resume data appears to be corrupted. Would you like to clear it so you can upload a new resume?'
                    );
                    
                    if (shouldCleanup) {
                        await fetch(`${API_BASE_URL}/profile/${user.email}/resume/cleanup`, {
                            method: 'DELETE'
                        });
                        alert('Resume data cleared. Please upload a new resume.');
                        setResumeFile(null);
                        return;
                    }
                }
                
                throw new Error(errorData.message || 'Failed to download resume');
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'resume.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading resume:', error);
            alert(`Resume download failed: ${error.message}`);
        }
    };


    const profileImageSrc = user?.email 
    ? `${API_BASE_URL}/profile/${user.email}/picture?${new Date().getTime()}`
    : '/default-avatar.png';

    return (
        <div className={ui.page}>
        <Header />
        <main className="flex-grow">
            <div className={ui.container}>
        <div className="profile-container max-w-5xl mx-auto bg-white rounded-xl shadow-lg">
            {/* Profile Header Section */}
            <div className="p-8">
                <div className="profile-header flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div className="profile-picture-container mb-4 md:mb-0">
                        <div className="relative">
                        <img
                            src={profileImageSrc}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/default-avatar.png';
                            }}
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                        />
                        {profileData.open_to_work ? (
                            <>
                                <div className="pointer-events-none absolute inset-0 rounded-full ring-4 ring-green-500"></div>
                                <div className="pointer-events-none absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[10px] px-2 py-0.5 rounded-full shadow">OPEN TO WORK</div>
                            </>
                        ) : null}
                        </div>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-800">
                        {profileData.firstName} {profileData.lastName}
                    </h1>
                    <div className="flex flex-col">
                    <span className="text-gray-600 font-medium">Applying For</span>
                    <span className="text-gray-800">{profileData.applyingPosition || "Not Specified"}</span>
                    </div>
                    <div className="profile-actions flex items-center gap-4">
                    {isPdfReady && user?.email && (
                        <>
                            <PDFDownloadLink
                                document={<ProfilePDF profileData={profileData} />}
                                fileName={`${profileData.firstName}_${profileData.lastName}_Profile.pdf`}
                                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all duration-300"
                            >
                                <Download size={20} />
                                Download PDF
                            </PDFDownloadLink>

                            <button 
                                className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-all duration-300" 
                                onClick={() => setIsEditOpen(true)}
                            >
                                <Edit3 size={20} />
                                Edit Profile
                            </button>
                            <button
                                onClick={handleToggleOpenToWork}
                                disabled={togglingOpen}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${profileData.open_to_work ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                            >
                                {profileData.open_to_work ? 'Open to Work: On' : 'Open to Work: Off'}
                            </button>
                        </>
                    )}
                    </div>
                </div>

                {/* Contact Information */}
                <div className="profile-contact mb-8 p-6 bg-gray-50 rounded-xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg-grid-cols-3 gap-4">
                        <div className="flex flex-col">
                            <span className="text-gray-600 font-medium">NIC</span>
                            <span className="text-gray-800">{profileData.nic || "Not Available"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-gray-600 font-medium">Location</span>
                            <span className="text-gray-800">{profileData.location || "Not Available"}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-gray-600 font-medium">Phone</span>
                            <span className="text-gray-800">{profileData.phoneCode} {profileData.phoneNumber}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-gray-600 font-medium">Email</span>
                            <span className="text-gray-800">{profileData.email}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-gray-600 font-medium">Industry</span>
                            <span className="text-gray-800">{profileData.industry || "Not Available"}</span>
                        </div>
                    </div>
                </div>
                
                       {/* Career History Section */}
                        {profileData.careers && profileData.careers.length > 0 && (
                        <div className="profile-section mb-8">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Career History
                            </h3>
                            <div className="space-y-4">
                                {profileData.careers.map((career, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">Company:</span> {career.companyName || "Not Available"}
                                                </p>
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">Position:</span> {career.jobPosition || "Not Available"}
                                                </p>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">Start Date:</span> {formatDate(career.startDate)}
                                                </p>
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">End Date:</span> {career.endDate ? formatDate(career.endDate) : "Present"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* Garment Experience Table (Web UI) */}
                        {(profileData.industry === 'Garment Sector') && (
                        <div className="profile-section mb-8">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Garment Experience
                            </h3>
                            <div className="p-4 bg-gray-50 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                {profileData.department && (
                                    <div>
                                        <span className="text-gray-600 font-medium">Department</span>
                                        <div className="text-gray-800">{profileData.department}</div>
                                    </div>
                                )}
                                {profileData.position && (
                                    <div>
                                        <span className="text-gray-600 font-medium">Designation</span>
                                        <div className="text-gray-800">{profileData.position}</div>
                                    </div>
                                )}
                            </div>

                            {Array.isArray(profileData.product_experiences) && profileData.product_experiences.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Product</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Machine</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Experience Level</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {profileData.product_experiences.flatMap(pe => (
                                                (pe.machines || []).map((m, idx) => (
                                                    <tr key={`${pe.product?.id || 'p'}-${m.id}-${idx}`}>
                                                        <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">
                                                            {pe.product?.name}
                                                            {pe.product?.code && (
                                                                <span className="ml-2 text-xs text-gray-400">({pe.product.code})</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">{m.name}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">{m.experience_level}</td>
                                                    </tr>
                                                ))
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">No garment experience details available.</div>
                            )}
                        </div>
                        )}

                        {/* Education Section */}
                        {profileData.education && profileData.education.length > 0 && (
                        <div className="profile-section mb-8">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Education
                            </h3>
                            <div className="space-y-4">
                                {profileData.education.map((edu, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">Institute:</span> {edu.instituteName || "Not Available"}
                                                </p>
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">Program:</span> {edu.degreeProgram || "Not Available"}
                                                </p>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">Start Date:</span> {formatDate(edu.startDate)}
                                                </p>
                                                <p className="text-gray-600">
                                                    <span className="font-semibold">End Date:</span> {edu.endDate ? formatDate(edu.endDate) : "Present"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* Licenses Section */}
                        {profileData.licenses && profileData.licenses.length > 0 && (
                        <div className="profile-section mb-8">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Licenses
                            </h3>
                            <div className="space-y-4">
                                {profileData.licenses.map((license, index) => (
                                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                                        <div className="space-y-2">
                                            <p className="text-gray-600">
                                                <span className="font-semibold">Name:</span> {license.name || "Not Available"}
                                            </p>
                                            <p className="text-gray-600">
                                                <span className="font-semibold">Issuer:</span> {license.issuer || "Not Available"}
                                            </p>
                                            <p className="text-gray-600">
                                                <span className="font-semibold">Issue Date:</span> {formatDate(license.date)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                         {/* Skills Section */}
                        <div className="profile-section">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                            	Skills
                            </h3>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex flex-wrap gap-2">
                                {Array.isArray(profileData.skills) && profileData.skills.length > 0 ? 
                                    profileData.skills.map((skill, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                                        >
                                            {skill.trim()}
                                        </span>
                                    ))
                                    : "Not Available"
                                }
                                </div>
                            </div>
                        </div>

                        {/* Garment Profile Section (read-only) */}
                        {profileData.industry === 'Garment Sector' && (
                        <div className="profile-section">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Garment Profile
                            </h3>
                            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                        <span className="text-gray-600 font-medium">Department</span>
                                        <span className="text-gray-800">{profileData.department || "Not Available"}</span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-gray-600 font-medium">Current Position</span>
                                        <span className="text-gray-800">{profileData.position || "Not Available"}</span>
                                    </div>
                                </div>
                                <div>
                                    <span className="text-gray-600 font-medium">Products</span>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {Array.isArray(profileData.products) && profileData.products.length > 0 ?
                                            profileData.products.map((p, idx) => (
                                                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">{p}</span>
                                            )) : "Not Available"}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-gray-600 font-medium">Machines</span>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {Array.isArray(profileData.machines) && profileData.machines.length > 0 ?
                                            profileData.machines.map((m, idx) => (
                                                <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">{m}</span>
                                            )) : "Not Available"}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Languages Section */}
                        <div className="profile-section">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Languages
                            </h3>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="flex flex-wrap gap-2">
                                {Array.isArray(profileData.languages) && profileData.languages.length > 0 ? 
                                    profileData.languages.map((language, index) => (
                                        <span 
                                            key={index}
                                            className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                                        >
                                            {language.trim()}
                                        </span>
                                    ))
                                    : "Not Available"
                                }
                                </div>
                            </div>
                        </div>

                        {/* Summary Section */}
                        <div className="profile-section">
                            <h3 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-4">
                                Summary
                            </h3>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-gray-600 leading-relaxed">
                                    {profileData.summary || "Add a personal summary to introduce yourself."}
                                </p>
                            </div>
                        </div>
                    </div>


            {/* Side Panel for Editing */}
            {isEditOpen && (
                <div className="side-panel fixed top-0 right-0 w-96 bg-white shadow-xl p-6 h-full overflow-y-auto z-50">
                    <div className="side-panel-header flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-semibold text-gray-800">Edit Profile</h2>
                        <button className="close-panel-btn text-3xl" onClick={() => setIsEditOpen(false)}>×</button>
                    </div>
                    <div className="side-panel-content">
                        {/* Basic Info */}
                        <div className="profile-basic-info mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Basic Info</h3>
                            
                            {/* Profile Picture with Separate Upload Button in Sidebar */}
                            <div className="relative mb-4 inline-block">
                                <div className="relative">
                                    <img
                                        src={`${API_BASE_URL}/profile/${user.email}/picture?${new Date().getTime()}`}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/default-avatar.png';
                                        }}
                                        alt="Profile"
                                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                                    />
                                    {profileData.open_to_work ? (
                                        <>
                                            <div className="pointer-events-none absolute inset-0 rounded-full ring-4 ring-green-500"></div>
                                            <div className="pointer-events-none absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-600 text-white text-[10px] px-2 py-0.5 rounded-full shadow">OPEN TO WORK</div>
                                        </>
                                    ) : null}
                                    <div className="flex gap-2 mt-2">
                                        <label className="cursor-pointer bg-indigo-600 text-white px-3 py-1 rounded-md text-sm hover:bg-indigo-700 transition-colors flex items-center gap-1">
                                            <Upload size={16} />
                                            Change Photo
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept="image/jpeg,image/png"
                                                onChange={handleProfilePictureUpload}
                                            />
                                        </label>
                                        {profilePicture && (
                                            <button 
                                                className="bg-red-600 text-white px-3 py-1 rounded-md text-sm hover:bg-red-700 transition-colors flex items-center gap-1"
                                                onClick={async () => {
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/profile/${user.email}/picture`, {
                                                            method: 'DELETE'
                                                        });
                                                        
                                                        if (!response.ok) {
                                                            throw new Error('Failed to remove profile picture');
                                                        }
                                                        
                                                        setProfilePicture(null);
                                                        setProfileData(prev => ({ ...prev, pictureUpdated: Date.now() }));
                                                        alert('Profile picture removed successfully!');
                                                    } catch (error) {
                                                        console.error('Error removing profile picture:', error);
                                                        alert('Error removing profile picture');
                                                    }
                                                }}
                                            >
                                                <Trash2 size={16} />
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">First Name:</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                    value={tempData.firstName || profileData.firstName} 
                                    onChange={(e) => handleFieldChange("firstName", e.target.value)} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">Last Name:</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                    value={tempData.lastName || profileData.lastName} 
                                    onChange={(e) => handleFieldChange("lastName", e.target.value)} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">NIC:</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                    value={tempData.nic || profileData.nic} 
                                    onChange={(e) => handleFieldChange("nic", e.target.value)} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">Location:</label>
                                {isSriLanka ? (
                                    <div className="grid grid-cols-1 gap-3">
                                        <select className="w-full p-3 border rounded-md" value={province} onChange={(e)=>{ setProvince(e.target.value); setDistrict(''); setDivisionalSecretariat(''); setGnd(''); setVillage(''); }}>
                                            <option value="">Select province</option>
                                            {provinceOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                                        </select>
                                        <select className="w-full p-3 border rounded-md" value={district} onChange={(e)=>{ setDistrict(e.target.value); setDivisionalSecretariat(''); setGnd(''); setVillage(''); }} disabled={!province}>
                                            <option value="">Select district</option>
                                            {districtOptions.map(d => (<option key={d} value={d}>{d}</option>))}
                                        </select>
                                        <select className="w-full p-3 border rounded-md" value={divisionalSecretariat} onChange={(e)=>{ setDivisionalSecretariat(e.target.value); setGnd(''); setVillage(''); }} disabled={!district}>
                                            <option value="">Select divisional secretariat</option>
                                            {dsOptions.map(ds => (<option key={ds} value={ds}>{ds}</option>))}
                                        </select>
                                        <select className="w-full p-3 border rounded-md" value={gnd} onChange={(e)=>{ setGnd(e.target.value); setVillage(''); }} disabled={!divisionalSecretariat}>
                                            <option value="">Select GND</option>
                                            {gndOptions.map(g => (<option key={g} value={g}>{g}</option>))}
                                        </select>
                                        <select className="w-full p-3 border rounded-md" value={village} onChange={(e)=> setVillage(e.target.value)} disabled={!gnd}>
                                            <option value="">Select village</option>
                                            {villageOptions.map(v => (<option key={v} value={v}>{v}</option>))}
                                        </select>
                                        <div className="text-xs text-gray-500">Composed: {(tempData.location || profileData.location || '')}</div>
                                    </div>
                                ) : (
                                    <input 
                                        type="text" 
                                        className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                        value={tempData.location || profileData.location} 
                                        onChange={(e) => handleFieldChange("location", e.target.value)} />
                                )}
                            </div>
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">Industry:</label>
                                <select 
                                    className="w-full p-3 border border-gray-300 rounded-md text-lg"
                                    value={(tempData.industry ?? profileData.industry) || ''}
                                    onChange={(e) => handleFieldChange('industry', e.target.value)}
                                >
                                    <option value="">Select industry</option>
                                    <option value="Garment Sector">Garment Sector</option>
                                    <option value="Construction">Construction</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">Phone Number:</label>
                                <input 
                                    type="text" 
                                    className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                    value={tempData.phoneNumber || profileData.phoneNumber} 
                                    onChange={(e) => handleFieldChange("phoneNumber", e.target.value)} />
                            </div>
                            <div className="mb-4">
                                <label className="block text-lg font-semibold text-gray-600">Position Applying For:</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="w-full p-3 border border-gray-300 rounded-md text-lg"
                                        placeholder="Type position title..."
                                        value={positionInputValue || tempData.applyingPosition || profileData.applyingPosition || ""}
                                        onChange={(e) => handlePositionInputChange(e.target.value)}
                                        onFocus={() => {
                                            setPositionInputValue(tempData.applyingPosition || profileData.applyingPosition || "");
                                            if ((tempData.applyingPosition || profileData.applyingPosition || "").length >= 2) {
                                                fetchPositionSuggestions(tempData.applyingPosition || profileData.applyingPosition || "");
                                            }
                                        }}
                                        onBlur={() => {
                                            // Delay hiding suggestions to allow for selection
                                            setTimeout(() => setShowSuggestions(false), 200);
                                        }}
                                    />
                                    {showSuggestions && positionSuggestions.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                            {positionSuggestions.map((suggestion, index) => (
                                                <div
                                                    key={index}
                                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors"
                                                    onClick={() => handleSuggestionSelect(suggestion)}
                                                >
                                                    {suggestion}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Garment-specific fields */}
                        {(tempData.industry ?? profileData.industry) === 'Garment Sector' && (
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Garment Profile</h3>
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-lg font-semibold text-gray-600">Department:</label>
                                    <select
                                        className="w-full p-3 border border-gray-300 rounded-md text-lg"
                                        value={(tempData.department ?? profileData.department) || ''}
                                        onChange={(e) => {
                                            handleFieldChange('department', e.target.value);
                                            // reset designation on dept change
                                            handleFieldChange('position', '');
                                        }}
                                    >
                                        <option value="">Select department</option>
                                        {deptOptions.map(d => (
                                            <option key={d.id} value={d.name}>{d.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-lg font-semibold text-gray-600">Designation:</label>
                                    <select
                                        className="w-full p-3 border border-gray-300 rounded-md text-lg"
                                        value={(tempData.position ?? profileData.position) || ''}
                                        onChange={(e) => handleFieldChange('position', e.target.value)}
                                        disabled={((tempData.department ?? profileData.department) || '') === ''}
                                    >
                                        <option value="">Select designation</option>
                                        {desgOptions.map(g => (
                                            <option key={g.id} value={g.name}>{g.name}</option>
                                        ))}
                                    </select>
                                </div>
                                {/* Editable Product & Machine Experience entries */}
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <label className="block text-lg font-semibold text-gray-600">Product & Machine Experience</label>
                                        <button
                                            type="button"
                                            className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                                            onClick={() => {
                                                const chosen = new Set(entries.map(e=>e.productId).filter(Boolean));
                                                const next = productOptions.find(p=>!chosen.has(p.id));
                                                const newEntry = { productId: next ? next.id : null, machineOptions: [], machineExperience: {} };
                                                setEntries(prev=>[...prev, newEntry]);
                                                if (next) loadMachinesForEntry(entries.length, next.id);
                                            }}
                                        >+ Add another</button>
                                    </div>
                                    {entries.map((entry, idx) => {
                                        const chosenProductIds = new Set(entries.map((e, i) => i===idx ? null : e.productId).filter(Boolean));
                                        const availableProducts = productOptions.filter(p => !chosenProductIds.has(p.id));
                                        return (
                                            <div key={idx} className="border rounded p-3 grid grid-cols-1 gap-3">
                                                <div>
                                                    <label className="text-sm text-gray-600">Product</label>
                                                    <select
                                                        className="w-full p-2 border rounded"
                                                        value={entry.productId || ''}
                                                        onChange={(e)=>{
                                                            const pid = e.target.value ? Number(e.target.value) : null;
                                                            setEntries(prev => prev.map((en,i)=> i===idx ? { ...en, productId: pid, machineExperience: {}, machineOptions: [] } : en));
                                                            if (pid) loadMachinesForEntry(idx, pid);
                                                        }}
                                                    >
                                                        <option value="">Select a product</option>
                                                        {availableProducts.map(p => (
                                                            <option key={p.id} value={p.id}>{p.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="text-sm text-gray-600">Machines</label>
                                                    <div className="mt-2 space-y-2">
                                                        {(!entry.machineOptions || entry.machineOptions.length === 0) && (
                                                            <div className="text-xs text-gray-500">{entry.productId ? 'No machines mapped to this product yet.' : 'Select a product first.'}</div>
                                                        )}
                                                        {entry.machineOptions.map(m => (
                                                            <div key={m.id} className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={typeof entry.machineExperience[m.id] !== 'undefined'}
                                                                    onChange={(e)=>{
                                                                        setEntries(prev => prev.map((en,i)=>{
                                                                            if (i!==idx) return en;
                                                                            const me = { ...(en.machineExperience||{}) };
                                                                            if (e.target.checked) me[m.id] = me[m.id] || 'Beginner'; else delete me[m.id];
                                                                            return { ...en, machineExperience: me };
                                                                        }));
                                                                    }}
                                                                />
                                                                <span className="flex-1 text-sm">{m.name}</span>
                                                                {typeof entry.machineExperience[m.id] !== 'undefined' && (
                                                                    <div className="flex flex-col gap-1 w-full max-w-md">
                                                                        <div className="flex items-center gap-3">
                                                                            <input
                                                                                type="range"
                                                                                min={0}
                                                                                max={6}
                                                                                step={1}
                                                                                value={["3m","6m","1y","2y","3y","4y","5y+"].indexOf(entry.machineExperience[m.id]) !== -1 ? ["3m","6m","1y","2y","3y","4y","5y+"].indexOf(entry.machineExperience[m.id]) : 0}
                                                                                onChange={(e)=>{
                                                                                    const labels = ["3m","6m","1y","2y","3y","4y","5y+"];
                                                                                    const val = labels[Number(e.target.value)] || '3m';
                                                                                    setEntries(prev => prev.map((en,i)=>{
                                                                                        if (i!==idx) return en;
                                                                                        return { ...en, machineExperience: { ...en.machineExperience, [m.id]: val } };
                                                                                    }));
                                                                                }}
                                                                                className="flex-1 accent-blue-600"
                                                                            />
                                                                            <span className="text-xs text-gray-700 w-12 text-right">{entry.machineExperience[m.id]}</span>
                                                                        </div>
                                                                        <div className="flex justify-between text-[10px] text-gray-400 px-1">
                                                                            {['3m','6m','1y','2y','3y','4y','5y+'].map(l => (
                                                                                <span key={l}>{l}</span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                {idx>0 && (
                                                    <div>
                                                        <button type="button" className="text-red-600 text-xs underline" onClick={()=>setEntries(prev=>prev.filter((_,i)=>i!==idx))}>Remove</button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        )}
                        {renderCareerFields()}
                        {renderEducationFields()}
                        {renderLicenseFields()}

                        <div className="flex items-center space-x-4 mb-6">
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => handleFileUpload(e, 'resume')}
                                className="hidden"
                                id="resume-upload"
                            />
                            <label
                                htmlFor="resume-upload"
                                className="cursor-pointer flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
                            >
                                <Upload size={20} />
                                <span>{resumeFile ? resumeFile.name : 'Upload Resume'}</span>
                            </label>
                            <button
                                onClick={handleResumeDownload}
                                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                            >
                                <FileDown size={20} />
                                <span>Download Resume</span>
                            </button>
                        </div>
            
                        {/* Skills input in sidebar */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Skills</h3>
                            <input 
                                type="text" 
                                className="w-full p-3 border rounded-md"
                                placeholder="Enter skills separated by commas"
                                value={(tempData.skills ?? (Array.isArray(profileData.skills) ? profileData.skills.join(', ') : '')) ?? ''}
                                onChange={(e) => handleFieldChange("skills", e.target.value)}
                            />
                        </div>

                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Languages</h3>
                            <input 
                                type="text" 
                                className="w-full p-3 border rounded-md"
                                placeholder="Enter languages separated by commas"
                                value={(tempData.languages ?? (Array.isArray(profileData.languages) ? profileData.languages.join(', ') : '')) ?? ''}
                                onChange={(e) => handleFieldChange("languages", e.target.value)}
                            />
                        </div>

                        {/* Summary */}
                        <div className="profile-summary mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Summary</h3>
                            <div className="mb-4">
                                <textarea 
                                    className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                    rows="4"
                                    value={(tempData.summary ?? profileData.summary) ?? ''} 
                                    onChange={(e) => handleFieldChange("summary", e.target.value)} />
                            </div>
                        </div>

                        <div className="flex justify-between">
                            <button 
                                className="save-changes-btn bg-indigo-600 text-white px-6 py-2 rounded-md text-lg hover:bg-indigo-700"
                                onClick={saveAllChanges}>
                                Save Changes
                            </button>
                            <button 
                                className="cancel-changes-btn bg-gray-300 text-black px-6 py-2 rounded-md text-lg hover:bg-gray-400"
                                onClick={() => setIsEditOpen(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>


    </div>
            </main>
            <Footer />
        </div>
);
};

export default Profile;